import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, BuildingIcon, UserIcon, CopyIcon, CheckIcon, Trash2Icon, LinkIcon, ClockIcon, UploadIcon } from 'lucide-react';
import { Supplier, mockSuppliers, InvitedSupplier, mockInvitedSuppliers } from '../utils/mockSuppliers';
export function Suppliers() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'owed' | 'invited'>('all');
  const [showArchived, setShowArchived] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [invitedSuppliers, setInvitedSuppliers] = useState<InvitedSupplier[]>(mockInvitedSuppliers);
  const filteredSuppliers = mockSuppliers.filter(supplier => {
    const matchesSearch = supplier.name.toLowerCase().includes(searchQuery.toLowerCase()) || supplier.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || activeTab === 'owed' && supplier.amountOwed !== 'THB 0.00';
    const matchesArchived = showArchived ? true : !supplier.isArchived;
    return matchesSearch && matchesTab && matchesArchived;
  });
  const filteredInvited = invitedSuppliers.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleCopyInviteLink = (supplier: InvitedSupplier) => {
    const fullLink = `${window.location.origin}${supplier.inviteLink}`;
    navigator.clipboard.writeText(fullLink);
    setCopiedId(supplier.id);
    setTimeout(() => setCopiedId(null), 2000);
  };
  const handleDeleteInvite = (id: string) => {
    setInvitedSuppliers(prev => prev.filter(s => s.id !== id));
  };
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-11664">
      {/* Header */}
      <div className="flex justify-between items-center" data-id="element-11665">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-11666">Suppliers</h1>
        <div className="flex items-center gap-3" data-id="element-11667">
          <button onClick={() => navigate('/supplier-bulk-upload')} className="flex items-center px-4 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm" data-id="element-11668">
            <UploadIcon size={16} className="mr-2" data-id="element-11669" />
            Upload
          </button>
          <button onClick={() => navigate('/supplier-form')} className="px-4 py-2 bg-[#F7941D] hover:bg-[#D97B06] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-11670">
            Add supplier
          </button>
        </div>
      </div>

      {/* Main Content Card */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-11671">
        {/* Controls Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 border-b border-gray-200 gap-4" data-id="element-11672">
          {/* Tabs */}
          <div className="flex space-x-6" data-id="element-11673">
            <button onClick={() => setActiveTab('all')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'all' ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11674">
              All
            </button>
            <button onClick={() => setActiveTab('owed')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 ${activeTab === 'owed' ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11675">
              Money owed to
            </button>
            <button onClick={() => setActiveTab('invited')} className={`pb-4 -mb-4 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'invited' ? 'border-[#F7941D] text-[#F7941D]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-11676">
              Invited
              {invitedSuppliers.length > 0 && <span className={`text-xs font-semibold px-1.5 py-0.5 rounded-full ${activeTab === 'invited' ? 'bg-[#F7941D]/10 text-[#F7941D]' : 'bg-gray-100 text-gray-500'}`} data-id="element-11677">
                  {invitedSuppliers.length}
                </span>}
            </button>
          </div>

          {/* Right Controls */}
          <div className="flex items-center space-x-6" data-id="element-11678">
            {activeTab !== 'invited' && <label className="flex items-center cursor-pointer" data-id="element-11679">
                <span className="text-sm text-gray-600 mr-3 font-medium" data-id="element-11680">
                  Show archived suppliers
                </span>
                <div className="relative" data-id="element-11681">
                  <input type="checkbox" className="sr-only" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} data-id="element-11682" />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${showArchived ? 'bg-[#F7941D]' : 'bg-gray-200'}`} data-id="element-11683"></div>
                  <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${showArchived ? 'transform translate-x-4' : ''}`} data-id="element-11684"></div>
                </div>
              </label>}

            <div className="relative w-64" data-id="element-11685">
              <SearchIcon size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-11686" />
              <input type="text" placeholder="Search by supplier" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#F7941D]/20 focus:border-[#F7941D] transition-colors" data-id="element-11687" />
            </div>
          </div>
        </div>

        {/* Active Suppliers Table */}
        {activeTab !== 'invited' && <div className="overflow-x-auto" data-id="element-11688">
            <table className="w-full text-left border-collapse" data-id="element-11689">
              <thead data-id="element-11690">
                <tr className="border-b border-gray-100" data-id="element-11691">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11692">
                    Supplier detail
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11693">
                    Type
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11694">
                    You owe them
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11695">
                    Source
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11696">
                    Date created
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-11697">
                {filteredSuppliers.map(supplier => <tr key={supplier.id} onClick={() => navigate(`/supplier-detail/${supplier.id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors group" data-id="element-11698">
                    <td className="px-6 py-4" data-id="element-11699">
                      <div className="text-sm font-medium text-gray-900 group-hover:text-[#F7941D] transition-colors" data-id="element-11700">
                        {supplier.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5" data-id="element-11701">
                        {supplier.email}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11702">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-11703">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-11704" />
                          Company
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-11705">
                          <UserIcon size={12} className="mr-1.5" data-id="element-11706" />
                          Individual
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-11707">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11708">
                        {supplier.amountOwed}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11709">
                      <div className="text-sm text-gray-500" data-id="element-11710">
                        {supplier.source}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-11711">
                      <div className="text-sm text-gray-500" data-id="element-11712">
                        {supplier.dateCreated}
                      </div>
                    </td>
                  </tr>)}
                {filteredSuppliers.length === 0 && <tr data-id="element-11713">
                    <td colSpan={5} className="px-6 py-12 text-center text-gray-500 text-sm" data-id="element-11714">
                      No suppliers found matching your criteria.
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}

        {/* Invited Suppliers Table */}
        {activeTab === 'invited' && <div className="overflow-x-auto" data-id="element-11715">
            <table className="w-full text-left border-collapse" data-id="element-11716">
              <thead data-id="element-11717">
                <tr className="border-b border-gray-100" data-id="element-11718">
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11719">
                    Supplier name
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11720">
                    Type
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11721">
                    Status
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11722">
                    Invited
                  </th>
                  <th className="px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider text-right" data-id="element-11723">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-11724">
                {filteredInvited.map(supplier => <tr key={supplier.id} className="group" data-id="element-11725">
                    <td className="px-6 py-4" data-id="element-11726">
                      <div className="text-sm font-medium text-gray-900" data-id="element-11727">
                        {supplier.name}
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-11728">
                      {supplier.supplierType === 'company' ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100" data-id="element-11729">
                          <BuildingIcon size={12} className="mr-1.5" data-id="element-11730" />
                          Company
                        </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100" data-id="element-11731">
                          <UserIcon size={12} className="mr-1.5" data-id="element-11732" />
                          Individual
                        </span>}
                    </td>
                    <td className="px-6 py-4" data-id="element-11733">
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-id="element-11734">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" data-id="element-11735"></span>
                        Pending
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-11736">
                      <div className="text-sm text-gray-900" data-id="element-11737">
                        {supplier.dateInvited}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1" data-id="element-11738">
                        <ClockIcon size={11} data-id="element-11739" />
                        {supplier.daysAgo} days ago
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-11740">
                      <div className="flex items-center justify-end gap-2" data-id="element-11741">
                        <button onClick={() => handleCopyInviteLink(supplier)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors" data-id="element-11742">
                          {copiedId === supplier.id ? <>
                              <CheckIcon size={13} className="mr-1.5 text-green-600" data-id="element-11743" />
                              Copied
                            </> : <>
                              <CopyIcon size={13} className="mr-1.5" data-id="element-11744" />
                              Copy link
                            </>}
                        </button>
                        <button onClick={() => handleDeleteInvite(supplier.id)} className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-white border border-gray-200 rounded-lg hover:bg-red-50 hover:border-red-200 transition-colors" data-id="element-11745">
                          <Trash2Icon size={13} className="mr-1.5" data-id="element-11746" />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>)}
                {filteredInvited.length === 0 && <tr data-id="element-11747">
                    <td colSpan={5} className="px-6 py-12 text-center text-sm" data-id="element-11748">
                      <div className="flex flex-col items-center gap-3" data-id="element-11749">
                        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center" data-id="element-11750">
                          <LinkIcon size={20} className="text-gray-400" data-id="element-11751" />
                        </div>
                        <div data-id="element-11752">
                          <p className="text-gray-700 font-medium" data-id="element-11753">
                            No pending invitations
                          </p>
                          <p className="text-gray-500 mt-0.5" data-id="element-11754">
                            Invite a supplier by clicking "Add supplier" and
                            choosing the invite option.
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>}
      </div>
    </div>;
}